import {Component, Input, OnInit, HostListener} from '@angular/core';
import {Observable} from 'rxjs';
import {ConfirmDialogService} from '../../../Services/confirm-dialog.service';
import {BsToastService} from '../../../Services/bs-toast-service';
import {CanComponentDeactivate, UnsavedChanges} from '../../../Interfaces/unsaved-changes.interface';
// Revert to default entry; allowlisted in angular.json to avoid warnings
import deepEqual from 'fast-deep-equal';


@Component({
  // Hybrid selector: allow usage as element OR attribute without extra wrapper nodes
  selector: 'app-unsaved-changes,[appUnsavedChanges]',
  exportAs: 'unsavedChanges',
  // As an attribute component on existing elements, we must project children
  // so that the host element's content still renders.
  template: '<ng-content></ng-content>',
  standalone: false
})
export class UnsavedChangesComponent implements OnInit, UnsavedChanges, CanComponentDeactivate {
  @Input() originalData: any;
  @Input() currentData: any;
  @Input() navigationWarningMessage = 'There are unsaved changes. Do you really want to leave?';
  @Input() cancelNavigationMessage = 'Leave Canceled';

  private hasUnsavedChanges = false;
  private isNavigatingAway = false;
  // One-shot suppression of guard checks (for programmatic navigations like Save Close / Save New)
  private suppressOnceFlag = false;

  constructor(
    public confirmDialogService: ConfirmDialogService,
    public notification: BsToastService
  ) {
  }

  ngOnInit(): void {
    // Initial check
    this.checkForChanges();
  }

  /**
   * Host listener for browser beforeunload event (refresh, close, navigate away)
   */
  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.checkForChanges()) {
      event.preventDefault();
      const message = this.navigationWarningMessage;
      (event as any).returnValue = message;
    }
    return undefined;
  }

  /**
   * Host listener for popstate events (back/forward buttons)
   * This handles browser back/forward buttons only
   */
  @HostListener('window:popstate', ['$event'])
  onPopState(event: PopStateEvent): void {
    if (this.checkForChanges() && !this.isNavigatingAway) {
      // Prevent default only if needed
      event.preventDefault();

      // Set flag to avoid multiple dialogs
      this.isNavigatingAway = true;

      // Show our custom dialog
      const messages: string[] = [this.navigationWarningMessage];
      this.confirmDialogService.confirmThis(
        messages,
        () => {
          // User confirmed they want to leave
          this.hasUnsavedChanges = false;
          this.isNavigatingAway = false;
          // Need to manually trigger the navigation since we've canceled the original
          window.history.back();
        },
        () => {
          // User canceled navigation
          this.notification.showSuccessToast(this.cancelNavigationMessage);
          this.isNavigatingAway = false;

          // Push a new state to cancel the back operation
          window.history.pushState(null, '', window.location.href);
        }
      );

      // Push a new history state to prevent the navigation
      window.history.pushState(null, '', window.location.href);
    }
  }

  /**
   * Checks if there are differences between original and current data
   * @returns boolean indicating if changes exist
   */
  public checkForChanges(): boolean {
    if (this.suppressOnceFlag) {
      // consume the one-shot suppression
      this.suppressOnceFlag = false;
      this.hasUnsavedChanges = false;
      return false;
    }
    if (!this.originalData || !this.currentData) {
      this.hasUnsavedChanges = false;
      return false;
    }

    this.hasUnsavedChanges = !deepEqual(this.originalData, this.currentData);
    return this.hasUnsavedChanges;
  }

  /**
   * Reset the component's state by making originalData match currentData
   * This should be called after successful save operations
   */
  public resetChanges(): void {
    if (this.currentData) {
      // Create a deep copy of the current data to set as the new original data
      this.originalData = JSON.parse(JSON.stringify(this.currentData));
      // Reset the flag
      this.hasUnsavedChanges = false;
    }
  }

  /**
   * Update the original data with new data
   * @param data The new data to set as original
   */
  public updateOriginalData(data: any): void {
    if (data) {
      this.originalData = JSON.parse(JSON.stringify(data));
      this.checkForChanges();
    }
  }

  /**
   * Update the current data
   * @param data The new current data
   */
  public updateCurrentData(data: any): void {
    if (data) {
      this.currentData = data;
      this.checkForChanges();
    }
  }

  /**
   * Suppress unsaved-changes check exactly once. Useful right after a successful save
   * when the component triggers a programmatic navigation (Save Close / Save New).
   */
  public suppressOnce(): void {
    this.suppressOnceFlag = true;
  }

  /**
   * Implements UnsavedChanges interface method for the guard to call
   * Simply returns whether there are changes or not - NO DIALOG HERE
   */
  canDeactivate(): boolean | Observable<boolean> {
    // With the new functional guard approach, we don't need dialog logic here
    // Just delegate to checkForChanges but invert the result
    // (true if no changes/safe to navigate, false if changes/unsafe to navigate)
    return !this.checkForChanges();
  }
}

